import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMoney0, fmtDate, fmtDateTime, statutLabel, statutColor } from "../lib/format";
import { Modal, Field, Badge, Spinner, EmptyState, Confirm, Tabs } from "../components/ui";
import { RefSelect, GROUPES_PAYS } from "../components/RefSelect";
import { Toast, OwnerCell, ChoiceCell } from "../components/QuickCells";

const TYPES = ["client", "revendeur", "fournisseur", "concurrent"];
const ORG_META = ["nom", "type", "pays", "adresse", "complement", "code_postal", "ville", "statut", "domaines", "marche", "parent_id", "owner_id", "partage_global"];
const AVEC_DOMAINES = ["revendeur", "fournisseur"];
const AVEC_MARCHE = ["client"];

function OrgForm({ org, orgs, users, refs, onSave, onClose, isManager }: any) {
  const [f, setF] = useState<any>({ type: "client", domaines: [], ...org });
  const [err, setErr] = useState<string | null>(null);
  const statuts = refs?.statut_by_type?.[f.type] || [];
  const listes = refs?.listes || {};
  const save = async () => {
    setErr(null);
    try {
      await onSave(f);
    } catch (e) {
      setErr(apiError(e));
    }
  };
  return (
    <Modal title={f.id ? `Organisation — ${f.nom}` : "Nouvelle organisation"} onClose={onClose} wide testId="org-modal">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Field label="Nom" className="col-span-2">
          <input data-testid="org-nom-input" className="nx-input" value={f.nom || ""} onChange={(e) => setF({ ...f, nom: e.target.value })} />
        </Field>
        <Field label="Type">
          <select data-testid="org-type-select" className="nx-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value, statut: "" })}>
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Statut">
          <select data-testid="org-statut-select" className="nx-input" value={f.statut || ""} onChange={(e) => setF({ ...f, statut: e.target.value })}>
            <option value="">—</option>
            {statuts.map((s: string) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Pays">
          <RefSelect testId="org-pays-select" entries={listes.pays || []} groupes={GROUPES_PAYS} value={f.pays} onChange={(v) => setF({ ...f, pays: v })} />
        </Field>
        {AVEC_MARCHE.includes(f.type) && (
          <Field label="Marché vertical">
            <RefSelect testId="org-marche-select" entries={listes.marche_vertical || []} value={f.marche} onChange={(v) => setF({ ...f, marche: v })} />
          </Field>
        )}
        <Field label="Adresse" className="col-span-2">
          <input className="nx-input" value={f.adresse || ""} onChange={(e) => setF({ ...f, adresse: e.target.value })} />
        </Field>
        <Field label="Complément">
          <input className="nx-input" value={f.complement || ""} onChange={(e) => setF({ ...f, complement: e.target.value })} />
        </Field>
        <Field label="Code postal">
          <input className="nx-input" value={f.code_postal || ""} onChange={(e) => setF({ ...f, code_postal: e.target.value })} />
        </Field>
        <Field label="Ville">
          <input className="nx-input" value={f.ville || ""} onChange={(e) => setF({ ...f, ville: e.target.value })} />
        </Field>
        <Field label="Société mère">
          <select className="nx-input" value={f.parent_id || ""} onChange={(e) => setF({ ...f, parent_id: e.target.value || null })}>
            <option value="">—</option>
            {orgs.filter((o: any) => o.id !== f.id).map((o: any) => <option key={o.id} value={o.id}>{o.nom}</option>)}
          </select>
        </Field>
        {AVEC_DOMAINES.includes(f.type) && (
          <Field label="Domaines" className="col-span-2">
            <div className="flex gap-3 pt-1.5" data-testid="org-domaines">
              {(refs?.domaines || []).map((d: string) => (
                <label key={d} className="flex items-center gap-1.5 text-sm">
                  <input
                    data-testid={`org-domaine-${d}`}
                    type="checkbox"
                    checked={(f.domaines || []).includes(d)}
                    onChange={(e) =>
                      setF({ ...f, domaines: e.target.checked ? [...(f.domaines || []), d] : (f.domaines || []).filter((x: string) => x !== d) })
                    }
                  />
                  {d}
                </label>
              ))}
            </div>
          </Field>
        )}
        {isManager && (
          <Field label="Propriétaire">
            <select data-testid="org-owner-select" className="nx-input" value={f.owner_id || ""} onChange={(e) => setF({ ...f, owner_id: e.target.value || null })}>
              <option value="">—</option>
              {users.map((u: any) => <option key={u.id} value={u.id}>{u.nom_complet}</option>)}
            </select>
          </Field>
        )}
      </div>
      {err && <p className="mt-2 text-sm text-danger" data-testid="org-form-error">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="nx-btn-ghost" onClick={onClose}>Annuler</button>
        <button data-testid="org-save-btn" className="nx-btn-primary" disabled={!f.nom} onClick={save}>Enregistrer</button>
      </div>
    </Modal>
  );
}

function OrgFiche({ orgId, users, refs, me, isManager, onToast, onClose, onEdit, reload }: any) {
  const [org, setOrg] = useState<any>(null);
  const [note, setNote] = useState("");
  const load = () => api.get(`/organisations/${orgId}`).then((r) => setOrg(r.data)).catch((e) => alert(apiError(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  if (!org) return null;
  const statutBy = refs?.statut_by_type || {};
  const paysOpts = (refs?.listes?.pays || []).filter((e: any) => e.actif !== false).map((e: any) => ({ value: e.libelle, label: e.libelle, groupe: e.groupe }));
  const canF = isManager || org.owner_id === me || (org.partages || []).some((p: any) => p.user_id === me);
  const canO = isManager || org.owner_id === me;
  const quick = async (patch: any) => {
    const prev = org;
    setOrg((o: any) => ({ ...o, ...patch }));
    try {
      const body: any = {};
      ORG_META.forEach((k) => (body[k] = org[k]));
      const { data } = await api.patch(`/organisations/${orgId}`, { ...body, ...patch });
      setOrg(data);
      reload && reload();
    } catch (e) {
      setOrg(prev);
      onToast && onToast(apiError(e));
    }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/notes/organisation/${orgId}`, { texte: note });
    setNote("");
    load();
  };
  const ind = org.indicateurs;
  return (
    <Modal title={`${org.nom} (${org.code || ""})`} onClose={onClose} wide testId="org-fiche">
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="org-fiche-quick">
        <ChoiceCell field="ftype" orgId={org.id} value={org.type} editable={canF}
          display={<Badge className="bg-green text-white capitalize">{org.type}</Badge>}
          options={TYPES.map((t) => ({ value: t, label: t }))}
          onSelect={(v) => quick({ type: v, statut: (statutBy[v] || []).includes(org.statut) ? org.statut : "" })} />
        <ChoiceCell field="fstatut" orgId={org.id} value={org.statut} editable={canF}
          display={<Badge>{org.statut || "Statut —"}</Badge>}
          options={(statutBy[org.type] || []).map((s: string) => ({ value: s, label: s }))}
          onSelect={(v) => quick({ statut: v })} />
        <ChoiceCell field="fpays" orgId={org.id} value={org.pays} editable={canF} groups={GROUPES_PAYS}
          display={<Badge className="bg-soft text-muted">{org.pays || "Pays —"}</Badge>}
          options={paysOpts} onSelect={(v) => quick({ pays: v })} />
        {AVEC_DOMAINES.includes(org.type) && (org.domaines || []).map((d: string) => <Badge key={d} className="bg-lime-soft text-green">{d}</Badge>)}
        {AVEC_MARCHE.includes(org.type) && org.marche && <Badge className="bg-lime-soft text-green" testId="org-fiche-marche">Marché vertical : {org.marche}</Badge>}
        {org.parent_nom && <Badge className="bg-soft text-muted">Mère : {org.parent_nom}</Badge>}
        <span className="inline-flex items-center gap-1 text-sm text-muted">Propriétaire :
          <OwnerCell org={org} users={(users || []).filter((u: any) => u.actif !== false)} editable={canO} onApply={(patch) => quick(patch)} />
        </span>
        <button data-testid="org-fiche-edit-btn" className="nx-btn-ghost ml-auto !px-2.5 !py-1 text-xs" onClick={() => onEdit(org)}>Modifier</button>
      </div>
      {org.adresse && <p className="mb-3 text-sm text-muted">{[org.adresse, org.complement, org.code_postal, org.ville].filter(Boolean).join(", ")}</p>}

      <div className="grid gap-4 md:grid-cols-2">
        <section>
          <h3 className="mb-2 text-sm font-bold text-green">Contacts rattachés</h3>
          {org.contacts.length === 0 ? <p className="text-sm text-muted">Aucun contact</p> : (
            <ul className="space-y-1">
              {org.contacts.map((c: any) => (
                <li key={c.id} className="rounded-lg bg-soft px-3 py-1.5 text-sm">
                  <span className="font-semibold">{c.prenom} {c.nom}</span>
                  <span className="text-muted"> — {c.titre || ""} {c.email ? `· ${c.email}` : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h3 className="mb-2 text-sm font-bold text-green">Devis liés</h3>
          {org.devis.length === 0 ? <p className="text-sm text-muted">Aucun devis</p> : (
            <ul className="space-y-1">
              {org.devis.slice(0, 6).map((d: any) => (
                <li key={d.id} className="flex items-center justify-between rounded-lg bg-soft px-3 py-1.5 text-sm">
                  <Link to={`/devis/${d.id}`} className="font-semibold text-green hover:underline">N° {d.numero} — {d.objet}</Link>
                  <Badge className={statutColor(d.statut)}>{statutLabel(d.statut)}</Badge>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h3 className="mb-2 text-sm font-bold text-green">Contrats liés</h3>
          {org.contrats.length === 0 ? <p className="text-sm text-muted">Aucun contrat</p> : (
            <ul className="space-y-1">
              {org.contrats.map((c: any) => (
                <li key={c.id} className="flex items-center justify-between rounded-lg bg-soft px-3 py-1.5 text-sm">
                  <span className="font-semibold">{c.reference} — {c.produit_nom} {c.sous_produit_nom || ""}</span>
                  <span className="text-muted">{fmtDate(c.date_echeance)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        {ind && (
          <section data-testid="fournisseur-indicateurs">
            <h3 className="mb-2 text-sm font-bold text-green">Indicateurs fournisseur</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-soft px-3 py-1.5">CA généré : <b>{fmtMoney0(ind.ca_genere)}</b></div>
              <div className="rounded-lg bg-soft px-3 py-1.5">BC : <b>{ind.nb_bc}</b></div>
              <div className="rounded-lg bg-soft px-3 py-1.5">Marge : <b>{fmtMoney0(ind.marge)}</b></div>
              <div className="rounded-lg bg-soft px-3 py-1.5">Encours : <b>{fmtMoney0(ind.encours)}</b></div>
              <div className="rounded-lg bg-soft px-3 py-1.5">Facturé : <b>{fmtMoney0(ind.facture)}</b></div>
              <div className="rounded-lg bg-soft px-3 py-1.5">Non payé : <b>{fmtMoney0(ind.non_paye)}</b></div>
            </div>
          </section>
        )}
      </div>

      <section className="mt-4">
        <h3 className="mb-2 text-sm font-bold text-green">Fil de notes</h3>
        <div className="mb-2 flex gap-2">
          <input data-testid="org-note-input" className="nx-input" placeholder="Ajouter une note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
          <button data-testid="org-note-add-btn" className="nx-btn-primary" onClick={addNote}>Ajouter</button>
        </div>
        <div className="max-h-48 space-y-1.5 overflow-y-auto">
          {org.notes.map((n: any) => (
            <div key={n.id} data-testid={`org-note-${n.id}`} className="rounded-lg border border-line bg-white px-3 py-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-green">{n.auteur_nom}</span>
                <span className="flex items-center gap-2 text-[11px] text-muted">
                  {fmtDateTime(n.created_at)}
                  <button data-testid={`org-note-delete-${n.id}`} className="text-danger hover:underline" onClick={async () => { await api.delete(`/notes/${n.id}`); load(); }}>Supprimer</button>
                </span>
              </div>
              <p>{n.texte}</p>
            </div>
          ))}
        </div>
      </section>
    </Modal>
  );
}

function ContactForm({ contact, orgs, refs, onSave, onClose }: any) {
  const [f, setF] = useState<any>({ ...contact });
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={f.id ? `Contact — ${f.prenom || ""} ${f.nom}` : "Nouveau contact"} onClose={onClose} testId="contact-modal">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Prénom">
          <input data-testid="contact-prenom-input" className="nx-input" value={f.prenom || ""} onChange={(e) => setF({ ...f, prenom: e.target.value })} />
        </Field>
        <Field label="Nom">
          <input data-testid="contact-nom-input" className="nx-input" value={f.nom || ""} onChange={(e) => setF({ ...f, nom: e.target.value })} />
        </Field>
        <Field label="Titre">
          <input className="nx-input" value={f.titre || ""} onChange={(e) => setF({ ...f, titre: e.target.value })} />
        </Field>
        <Field label="Email">
          <input data-testid="contact-email-input" className="nx-input" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </Field>
        <Field label="Téléphone">
          <input className="nx-input" value={f.telephone || ""} onChange={(e) => setF({ ...f, telephone: e.target.value })} />
        </Field>
        <Field label="Pays">
          <RefSelect testId="contact-pays-select" entries={refs?.listes?.pays || []} groupes={GROUPES_PAYS} value={f.pays} onChange={(v) => setF({ ...f, pays: v })} />
        </Field>
        <Field label="Organisation" className="col-span-2">
          <select data-testid="contact-org-select" className="nx-input" value={f.organisation_id || ""} onChange={(e) => setF({ ...f, organisation_id: e.target.value || null })}>
            <option value="">—</option>
            {orgs.map((o: any) => <option key={o.id} value={o.id}>{o.nom}</option>)}
          </select>
        </Field>
      </div>
      {err && <p className="mt-2 text-sm text-danger">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="nx-btn-ghost" onClick={onClose}>Annuler</button>
        <button data-testid="contact-save-btn" className="nx-btn-primary" disabled={!f.nom} onClick={async () => { setErr(null); try { await onSave(f); } catch (e) { setErr(apiError(e)); } }}>Enregistrer</button>
      </div>
    </Modal>
  );
}

export default function Repertoire() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "organisations");
  const [orgs, setOrgs] = useState<any[] | null>(null);
  const [fournisseurs, setFournisseurs] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [refs, setRefs] = useState<any>(null);
  const [filters, setFilters] = useState<any>({});
  const [editOrg, setEditOrg] = useState<any>(null);
  const [ficheOrg, setFicheOrg] = useState<string | null>(params.get("org"));
  const [editContact, setEditContact] = useState<any>(null);
  const [delOrg, setDelOrg] = useState<any>(null);
  const [delContact, setDelContact] = useState<any>(null);
  const isManager = user?.role === "manager";
  const me = user?.id;
  const [toast, setToast] = useState<{ msg: string; kind?: string } | null>(null);
  const activeUsers = useMemo(() => users.filter((u: any) => u.actif !== false), [users]);
  const canFiche = (o: any) => isManager || o.owner_id === me || (o.partages || []).some((p: any) => p.user_id === me);
  const canOwners = (o: any) => isManager || o.owner_id === me;
  const statutBy = refs?.statut_by_type || {};
  const paysOpts = useMemo(
    () => (refs?.listes?.pays || []).filter((e: any) => e.actif !== false).map((e: any) => ({ value: e.libelle, label: e.libelle, groupe: e.groupe })),
    [refs]
  );

  const optimisticRow = (o: any, patch: any) => {
    const n = { ...o, ...patch };
    if (patch.owner_id !== undefined) {
      const ou = users.find((u: any) => u.id === patch.owner_id);
      if (ou) { n.owner_initiales = ou.initiales; n.owner_nom = ou.nom_complet; }
    }
    if (patch.partages !== undefined) {
      n.partages = (patch.partages || []).map((id: string) => {
        const u = users.find((x: any) => x.id === id);
        return { user_id: id, initiales: u?.initiales, nom: u?.nom_complet };
      });
    }
    return n;
  };

  const patchOrg = async (o: any, patch: any) => {
    const prev = orgs;
    setOrgs((cur) => (cur || []).map((x) => (x.id === o.id ? optimisticRow(x, patch) : x)));
    try {
      const body: any = {};
      ORG_META.forEach((k) => (body[k] = o[k]));
      const { data } = await api.patch(`/organisations/${o.id}`, { ...body, ...patch });
      setOrgs((cur) => (cur || []).map((x) => (x.id === o.id ? data : x)));
    } catch (e) {
      setOrgs(prev);
      setToast({ msg: apiError(e), kind: "error" });
    }
  };

  const load = () => {
    api.get("/organisations").then((r) => setOrgs(r.data));
    api.get("/organisations/fournisseurs").then((r) => setFournisseurs(r.data));
    api.get("/contacts").then((r) => setContacts(r.data));
  };
  useEffect(() => {
    // F3: au montage on ne charge que les organisations (+ users/referentiels).
    // Fournisseurs et contacts sont charges a la demande (ouverture de leur onglet).
    api.get("/organisations").then((r) => setOrgs(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/referentiels").then((r) => setRefs(r.data));
  }, []);

  useEffect(() => {
    if (tab === "fournisseurs" && fournisseurs.length === 0)
      api.get("/organisations/fournisseurs").then((r) => setFournisseurs(r.data));
    if (tab === "contacts" && contacts.length === 0)
      api.get("/contacts").then((r) => setContacts(r.data));
  }, [tab]);

  useEffect(() => {
    const n = params.get("new");
    if (n === "org") setEditOrg({});
    if (n === "contact") setEditContact({});
    if (n) {
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const filtered = useMemo(() => {
    if (!orgs) return [];
    return orgs.filter(
      (o) =>
        (!filters.type || o.type === filters.type) &&
        (!filters.pays || (o.pays || "").toLowerCase().includes(filters.pays.toLowerCase())) &&
        (!filters.statut || o.statut === filters.statut) &&
        (!filters.q || o.nom.toLowerCase().includes(filters.q.toLowerCase()))
    );
  }, [orgs, filters]);

  if (!orgs || !refs) return <Spinner />;

  const saveOrg = async (f: any) => {
    const { partages, owner_nom, owner_initiales, parent_nom, nb_contacts, indicateurs, contacts, devis, contrats, notes, bons, ...payload } = f;
    if (f.id) await api.patch(`/organisations/${f.id}`, payload);
    else await api.post("/organisations", payload);
    setEditOrg(null);
    load();
  };
  const saveContact = async (f: any) => {
    if (f.id) await api.patch(`/contacts/${f.id}`, f);
    else await api.post("/contacts", f);
    setEditContact(null);
    load();
  };

  return (
    <div data-testid="repertoire-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-green">Répertoire</h1>
        <div className="flex gap-2">
          <button data-testid="org-new-btn" className="nx-btn-primary" onClick={() => setEditOrg({})}>Nouvelle organisation</button>
          <button data-testid="contact-new-btn" className="nx-btn-ghost" onClick={() => setEditContact({})}>Nouveau contact</button>
        </div>
      </div>
      <Tabs
        tabs={[
          { key: "organisations", label: "Organisations", count: orgs.length },
          { key: "fournisseurs", label: "Fournisseurs", count: fournisseurs.length },
          { key: "contacts", label: "Contacts", count: contacts.length },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "organisations" && (
        <div className="nx-card overflow-x-auto">
          <div className="flex flex-wrap gap-2 border-b border-line p-3">
            <input data-testid="org-filter-q" className="nx-input max-w-[200px]" placeholder="Rechercher…" value={filters.q || ""} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            <select data-testid="org-filter-type" className="nx-input max-w-[160px]" value={filters.type || ""} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
              <option value="">Tous types</option>
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <input className="nx-input max-w-[150px]" placeholder="Pays…" value={filters.pays || ""} onChange={(e) => setFilters({ ...filters, pays: e.target.value })} />
          </div>
          {filtered.length === 0 ? <EmptyState message="Aucune organisation" /> : (
            <table className="nx-table">
              <thead><tr><th>Nom</th><th>Type</th><th>Pays</th><th>Statut</th><th>Domaines</th><th>Société mère</th><th>Propriétaire</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} data-testid={`org-row-${o.id}`}>
                    <td>
                      <button data-testid={`org-open-${o.id}`} className="font-bold text-green underline-offset-2 hover:underline" onClick={() => setFicheOrg(o.id)}>{o.nom}</button>
                    </td>
                    <td className="capitalize">
                      <ChoiceCell field="type" orgId={o.id} value={o.type} display={<span className="capitalize">{o.type}</span>} editable={canFiche(o)}
                        options={TYPES.map((t) => ({ value: t, label: t }))}
                        onSelect={(v) => patchOrg(o, { type: v, statut: (statutBy[v] || []).includes(o.statut) ? o.statut : "" })} />
                    </td>
                    <td>
                      <ChoiceCell field="pays" orgId={o.id} value={o.pays} editable={canFiche(o)} options={paysOpts} groups={GROUPES_PAYS} onSelect={(v) => patchOrg(o, { pays: v })} />
                    </td>
                    <td>
                      <ChoiceCell field="statut" orgId={o.id} value={o.statut}
                        display={o.statut ? <Badge>{o.statut}</Badge> : undefined} editable={canFiche(o)}
                        options={(statutBy[o.type] || []).map((s: string) => ({ value: s, label: s }))}
                        onSelect={(v) => patchOrg(o, { statut: v })} />
                    </td>
                    <td className="space-x-1">{AVEC_DOMAINES.includes(o.type) && (o.domaines || []).map((d: string) => <Badge key={d} className="bg-lime-soft text-green">{d}</Badge>)}</td>
                    <td>{o.parent_nom}</td>
                    <td>
                      <OwnerCell org={o} users={activeUsers} editable={canOwners(o)} onApply={(patch) => patchOrg(o, patch)} />
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <button data-testid={`org-edit-btn-${o.id}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => setEditOrg({ ...o })}>Éditer</button>
                        <button data-testid={`org-delete-btn-${o.id}`} className="nx-btn-danger !px-2 !py-1 text-xs" onClick={() => setDelOrg(o)}>Archiver</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "fournisseurs" && (
        <div className="nx-card overflow-x-auto">
          {fournisseurs.length === 0 ? <EmptyState message="Aucun fournisseur" /> : (
            <table className="nx-table">
              <thead><tr><th>Nom</th><th>Pays</th><th>Statut</th>{isManager && <><th className="text-right">CA généré</th><th className="text-right">BC</th><th className="text-right">Marge</th><th className="text-right">Encours</th><th className="text-right">Facturé</th><th className="text-right">Payé</th><th className="text-right">Non payé</th></>}<th className="text-right">Actions</th></tr></thead>
              <tbody>
                {fournisseurs.map((f) => (
                  <tr key={f.id} data-testid={`fournisseur-row-${f.id}`}>
                    <td><button className="font-bold text-green underline-offset-2 hover:underline" onClick={() => setFicheOrg(f.id)}>{f.nom}</button></td>
                    <td>{f.pays}</td>
                    <td>{f.statut && <Badge>{f.statut}</Badge>}</td>
                    {isManager && f.indicateurs && (
                      <>
                        <td className="text-right font-semibold">{fmtMoney0(f.indicateurs.ca_genere)}</td>
                        <td className="text-right">{f.indicateurs.nb_bc}</td>
                        <td className="text-right">{fmtMoney0(f.indicateurs.marge)}</td>
                        <td className="text-right">{fmtMoney0(f.indicateurs.encours)}</td>
                        <td className="text-right">{fmtMoney0(f.indicateurs.facture)}</td>
                        <td className="text-right">{fmtMoney0(f.indicateurs.paye)}</td>
                        <td className="text-right">{fmtMoney0(f.indicateurs.non_paye)}</td>
                      </>
                    )}
                    <td className="text-right">
                      <button className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => setEditOrg({ ...f })}>Éditer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "contacts" && (
        <div className="nx-card overflow-x-auto">
          {contacts.length === 0 ? <EmptyState message="Aucun contact" /> : (
            <table className="nx-table">
              <thead><tr><th>Prénom</th><th>Nom</th><th>Titre</th><th>Email</th><th>Téléphone</th><th>Pays</th><th>Organisation</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                    <td>{c.prenom}</td>
                    <td className="font-semibold">{c.nom}</td>
                    <td>{c.titre}</td>
                    <td>{c.email}</td>
                    <td>{c.telephone}</td>
                    <td>{c.pays}</td>
                    <td>{c.organisation_nom}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <button data-testid={`contact-edit-btn-${c.id}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => setEditContact({ ...c })}>Éditer</button>
                        <button data-testid={`contact-delete-btn-${c.id}`} className="nx-btn-danger !px-2 !py-1 text-xs" onClick={() => setDelContact(c)}>Archiver</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editOrg && <OrgForm org={editOrg} orgs={orgs} users={users} refs={refs} isManager={isManager} onSave={saveOrg} onClose={() => setEditOrg(null)} />}
      {ficheOrg && <OrgFiche orgId={ficheOrg} users={activeUsers} refs={refs} me={me} isManager={isManager} onToast={(msg: string) => setToast({ msg, kind: "error" })} onClose={() => setFicheOrg(null)} onEdit={(o: any) => { setFicheOrg(null); setEditOrg(o); }} reload={load} />}
      {editContact && <ContactForm contact={editContact} orgs={orgs} refs={refs} onSave={saveContact} onClose={() => setEditContact(null)} />}
      {delOrg && (
        <Confirm
          message={`Archiver l'organisation « ${delOrg.nom} » ? L'archivage sera refusé si des devis, contrats ou bons de commande y sont liés.`}
          onConfirm={async () => {
            try {
              await api.delete(`/organisations/${delOrg.id}`);
              setDelOrg(null);
              load();
            } catch (e) {
              setDelOrg(null);
              alert(apiError(e));
            }
          }}
          onCancel={() => setDelOrg(null)}
        />
      )}
      {delContact && (
        <Confirm
          message={`Archiver le contact « ${delContact.prenom || ""} ${delContact.nom} » ?`}
          onConfirm={async () => {
            await api.delete(`/contacts/${delContact.id}`);
            setDelContact(null);
            load();
          }}
          onCancel={() => setDelContact(null)}
        />
      )}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
